import { useEffect, useMemo } from "react";
import useSearchStore, { defaultFilter } from "@/stores/useSearchStore";
import { CompanyEntryDto } from "@advanced-sales-technologies/backend-client/models/CompanyEntryDto";
import isEqual from "lodash.isequal";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { SearchParams } from "@/hooks/api/search/useInfiniteSearch";
import Button from "@/components/atoms/Button";
import Spinner from "@/components/atoms/Spinner";
import FilterSection from "@/components/molecules/filter/FilterSection";
import MakeDynamicListFromSearch from "@/components/molecules/search/MakeDynamicListFromSearch";

type SearchSidebarProps = {
  isLoading: boolean;
  changedCompanies: CompanyEntryDto[];
};

const SearchSidebar = ({ isLoading, changedCompanies }: SearchSidebarProps) => {
  const { t } = useTranslation();
  const { setFilter, resetFilter, filter } = useSearchStore((state) => state);

  const isDirty = useMemo(() => !isEqual(filter, defaultFilter), [filter]);

  const {
    reset,
    watch,
    formState: { isValid, isSubmitting },
  } = useFormContext<SearchParams>();

  useEffect(() => {
    const subscription = watch((value) => {
      setFilter(value as SearchParams);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <>
      <div className={"sticky top-0 z-50 flex items-center justify-between bg-white pb-2 pt-6"}>
        <h3 className={"font-medium text-primary"}>{t("search.addFilter")}</h3>
        {isDirty && (
          <Button
            label={t("search.clearFilters")}
            size={"2xs"}
            variants={"text"}
            color={"brand"}
            className={"!py-0 !pr-0"}
            onClick={() => {
              reset(defaultFilter);
              resetFilter();
            }}
            data-testid={"clear-filters"}
          />
        )}
      </div>

      <div className="flex-1 pt-1" style={{ scrollbarWidth: "thin" }}>
        <FilterSection />
        <div className={"sticky bottom-16 bg-white pt-1"}>
          <MakeDynamicListFromSearch filter={filter} changedCompanies={changedCompanies} />
        </div>
      </div>

      <div className={"sticky bottom-0 bg-white pb-4 pt-2"}>
        <Button
          label={t("search.searchButton")}
          isLoading={isLoading}
          className={"w-full"}
          color={"brand"}
          variants={"primary"}
          type={"submit"}
          disabled={!isValid || isSubmitting || !isDirty}
        />
      </div>
    </>
  );
};

export default SearchSidebar;
